// Activity: site traffic (first-party daily counts, no personal data), the worksheets made, and the
// generator's pool and log, as tabs of one page.
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ALL_TOPICS, unitOf } from '@mathladder/shared';
import { api, type Doc } from '../api';
import { ago, count, fmtDateTime, useLoad, usePaged, useTitle } from '../lib';
import { Alert, Card, Empty, Json, Loading, Page, Pager, Stat, TableCard } from '../ui';
import { pageName } from './overview';

const TABS = [{ to: '/activity/traffic', label: 'Traffic' }, { to: '/activity/documents', label: 'Worksheets' }, { to: '/activity/generator', label: 'Generator' }];

// ---------------------------------------------------------------- traffic
interface Traffic {
  days: number; totals: { views: number; downloads: number };
  daily: { day: string; views: number; downloads: number }[];
  pages: { path: string; n: number }[]; downloads: { path: string; n: number }[]; refs: { ref: string; n: number }[];
}
const FILES = new Map<string, string>([
  ...ALL_TOPICS.map((t) => [t.path, t.name] as [string, string]),
  ['/files/placement-test', 'Placement test'],
]);
/** "/files/long-division/two-digit-divisors.pdf" → the topic's name; "/files/placement-test.pdf" → the placement test. */
const fileName = (f: string) => {
  const bare = f.replace(/(-answer-key)?\.pdf$/, '');
  const page = bare === '/files/placement-test' ? bare : bare.replace(/^\/files\/([^/]+)\/([^/]*)$/, '/worksheets/$1/$2/');
  return `${FILES.get(page) ?? f}${f.includes('answer-key') ? ' (key)' : ''}`;
};

export function TrafficPage() {
  useTitle('Traffic');
  const [days, setDays] = useState('30');
  const t = useLoad(() => api.get<Traffic>(`/admin/traffic?days=${days}`), [days]);
  const max = Math.max(1, ...(t.data?.daily.map((d) => d.views) ?? [1]));
  return (
    <Page title="Activity" tabs={TABS}
      actions={<span className="seg" role="group" aria-label="Period">{[['7', '7 days'], ['30', '30 days'], ['90', '90 days'], ['365', 'Year']].map(([v, l]) => <button key={v} type="button" aria-pressed={days === v} onClick={() => setDays(v)}>{l}</button>)}</span>}>
      <Alert>{t.error}</Alert>
      {!t.data ? <Loading /> : (
        <>
          <div className="stats">
            <Stat label="Page views" value={count(t.data.totals.views)} trend={t.data.daily.map((d) => d.views)} foot="Counted by the site: no cookies, no IP addresses" />
            <Stat label="Free downloads" value={count(t.data.totals.downloads)} trend={t.data.daily.map((d) => d.downloads)} foot="Worksheets and answer keys" />
            <Card title="Views per day">
              <div className="traffic-bars" aria-label="Page views per day">
                {t.data.daily.map((d) => <span key={d.day} title={`${d.day}: ${d.views} views, ${d.downloads} downloads`} style={{ height: `${(d.views / max) * 100}%` }} />)}
              </div>
            </Card>
          </div>
          <div className="cols3">
            <Ranked title="Most viewed pages" rows={t.data.pages.map((r) => ({ key: r.path, label: pageName(r.path), sub: r.path, n: r.n }))} />
            <Ranked title="Most downloaded" rows={t.data.downloads.map((r) => ({ key: r.path, label: fileName(r.path), n: r.n }))} />
            <Ranked title="Where visitors came from" rows={t.data.refs.map((r) => ({ key: r.ref, label: r.ref, n: r.n }))} />
          </div>
        </>
      )}
    </Page>
  );
}

function Ranked({ title, rows }: { title: string; rows: { key: string; label: string; sub?: string; n: number }[] }) {
  const top = Math.max(1, ...rows.map((r) => r.n));
  return (
    <Card title={title} flush>
      {!rows.length ? <Empty>Nothing yet.</Empty> : (
        <ul className="rows ranked">
          {rows.map((r) => (
            <li key={r.key}><div className="rowi" style={{ ['--w' as string]: `${(r.n / top) * 100}%` }}>
              <span className="min0"><span className="truncate" style={{ display: 'block' }}>{r.label}</span>{r.sub && r.sub !== r.label && <span className="sub mono">{r.sub}</span>}</span>
              <span className="end">{count(r.n)}</span>
            </div></li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- worksheets made
type AdminDoc = Doc & { email: string | null; ip: string | null; errorDetail: string | null };
const DOC_STATUS: [string, string][] = [['', 'All'], ['failed', 'Failed'], ['queued', 'Queued'], ['running', 'Running'], ['done', 'Done']];

export function Documents() {
  useTitle('Worksheets');
  const [p, setP] = useSearchParams();
  const status = p.get('status') ?? '';
  const list = usePaged<AdminDoc>(`/admin/documents${status ? `?status=${status}` : ''}`, 'documents');
  return (
    <Page title="Activity" tabs={TABS}>
      <TableCard
        toolbar={<span className="seg" role="group" aria-label="Status">{DOC_STATUS.map(([v, l]) => <button key={v} type="button" aria-pressed={status === v} onClick={() => setP(v ? { status: v } : {})}>{l}</button>)}</span>}
        pager={list.rows && <Pager offset={list.offset} total={list.total} shown={list.rows.length} onPage={list.setOffset} />}
      >
        <Alert>{list.error}</Alert>
        {!list.rows ? <Loading /> : !list.rows.length ? <Empty>None.</Empty> : (
          <table>
            <thead><tr><th>Made</th><th>Worksheet</th><th>Account</th><th>Status</th><th className="num">Pages</th><th>Error</th></tr></thead>
            <tbody>
              {list.rows.map((d) => (
                <tr key={d.id}>
                  <td className="nowrap muted">{ago(d.createdAt)}</td>
                  <td>{d.title}</td>
                  <td>{d.email ?? <span className="muted">free</span>}</td>
                  <td><span className={`badge ${d.status === 'failed' ? 'badge-bad' : d.status === 'done' ? '' : 'badge-warn'}`}>{d.status}</span></td>
                  <td className="num">{d.pages ?? ''}</td>
                  <td className="small">{d.errorDetail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableCard>
    </Page>
  );
}

// ---------------------------------------------------------------- generator
interface PoolStage { stage: number; ok: number; failed: number; slots: number; problems: number; warnings: number; avg_ms: number | null; served: number }
interface GenEntry { id: number; at: string; level: string; stage: number | null; kind: string | null; step: number | null; sheet: number | null; variant: number | null; message: string; detail: unknown }
const LEVELS: [string, string][] = [['fail', 'Failed checks'], ['error', 'Errors'], ['warn', 'Warnings'], ['', 'Everything']];
const where = (x: GenEntry) => (x.stage == null ? '' : `${unitOf(x.stage)?.name ?? `Unit ${x.stage}`}${x.step ? ` · topic ${x.step}` : ''}${x.sheet ? ` · sheet ${x.sheet}` : ''}${x.variant != null ? ` · v${x.variant}` : ''}`);

export function Generator() {
  useTitle('Generator');
  const pool = useLoad(() => api.get<{ stages: PoolStage[] }>('/admin/pool'));
  const [level, setLevel] = useState('fail');
  const log = usePaged<GenEntry>(`/admin/gen-log${level ? `?level=${level}` : ''}`, 'entries');
  const tot = pool.data?.stages.reduce((a, s) => ({ ok: a.ok + s.ok, failed: a.failed + s.failed, served: a.served + s.served }), { ok: 0, failed: 0, served: 0 });
  const onPage = useCallback((o: number) => log.setOffset(o), [log]);
  return (
    <Page title="Activity" tabs={TABS} sub={tot ? `${count(tot.ok)} verified versions in the pool · ${count(tot.served)} served · ${count(tot.failed)} failed` : undefined}>
      <div className="split2 wide-left">
        <TableCard
          toolbar={<span className="seg" role="group" aria-label="Log level">{LEVELS.map(([v, l]) => <button key={v} type="button" aria-pressed={level === v} onClick={() => setLevel(v)}>{l}</button>)}</span>}
          pager={log.rows && <Pager offset={log.offset} total={log.total} shown={log.rows.length} onPage={onPage} />}
        >
          <Alert>{log.error}</Alert>
          {!log.rows ? <Loading /> : !log.rows.length ? <Empty>Nothing logged{level === 'fail' ? ': every version passed verification' : ''}.</Empty> : (
            <table>
              <thead><tr><th>When</th><th>Where</th><th>Message</th></tr></thead>
              <tbody>
                {log.rows.map((e) => (
                  <tr key={e.id}>
                    <td className="nowrap muted">{fmtDateTime(e.at)}</td>
                    <td className="small">{where(e)}</td>
                    <td><span className={`badge ${e.level === 'fail' || e.level === 'error' ? 'badge-bad' : e.level === 'warn' ? 'badge-warn' : ''}`}>{e.level}</span> {e.message}<Json value={e.detail} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TableCard>
        <TableCard toolbar={<strong className="small">Pool by unit</strong>}>
          <Alert>{pool.error}</Alert>
          {!pool.data ? <Loading /> : !pool.data.stages.length ? <Empty>The pool is empty. Is the worker running?</Empty> : (
            <table>
              <thead><tr><th>Unit</th><th className="num">Verified</th><th className="num">Failed</th><th className="num">Served</th><th className="num">ms</th></tr></thead>
              <tbody>
                {pool.data.stages.map((s) => (
                  <tr key={s.stage}>
                    <td className="small">{unitOf(s.stage)?.name ?? s.stage}</td>
                    <td className="num">{count(s.ok)}</td>
                    <td className="num" style={s.failed ? { color: 'var(--bad)', fontWeight: 700 } : undefined}>{s.failed || ''}</td>
                    <td className="num">{count(s.served)}</td>
                    <td className="num muted">{s.avg_ms ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TableCard>
      </div>
    </Page>
  );
}
